import React from "react";
import { FormikProps, FormikValues } from "formik";

import { HOMEPAGE } from "@config/testIds/app";

import { LOADING_STATE } from "@typings/reduxThunkActions";
import { SERVICE_TYPE } from "@typings/globals";
import { SMARTLOOK_IDS } from "@typings/smartlookIds";

import { TestWrapper } from "@ui/Atoms/TestWrapper";
import { Variant } from "@ui/Atoms/Button";
import { SelectOption } from "@ui/Atoms/Form/SelectExtended";
import { FormikInputWithError } from "@ui/Molecules/FormikInputWithError";
import { FormikSelectExtended } from "@ui/Molecules/FormikSelectExtended";

import { useBrowserRerender } from "@hooks/useBrowserRerender";

import { FIELDS, handleOnInputChange } from "./StartBookingForm.helpers";
import * as Styled from "./StartBookingFormView.styled";

interface BasicProps {
  serviceType: SERVICE_TYPE;
  translations: {
    fieldPlaceholder: string;
    buttonLabel: string;
  };
  autoFocus?: boolean;
  buttonVariant?: Variant;
  isHorizontal?: boolean;
  buttonFitContent?: boolean;
}

interface Props extends BasicProps {
  className?: string;
  formikProps: FormikProps<FormikValues>;
  zipSuggestions: SelectOption[];
  loadingSuggestionsState: LOADING_STATE;
  setZipSuggestions: (value: SelectOption[]) => void;
  setLoadingSuggestionsState: (state: LOADING_STATE) => void;
}

const StartBookingFormView = (props: Props): React.ReactElement => {
  const isBrowser = useBrowserRerender();

  const [abortController, setAbortController] =
    React.useState<null | AbortController>(null);

  const fallbackFormProps = !isBrowser
    ? {
        action: "/en/submit-zip-code",
        method: "POST",
      }
    : {};

  React.useEffect(() => {
    props.setZipSuggestions([]);
    props.formikProps.setFieldValue(FIELDS.ZIPCODE, "");
  }, [props.serviceType]);

  // BFCACHE handler
  React.useEffect(() => {
    const clearField = (event: PageTransitionEvent) => {
      if (!event.persisted) return;

      props.setZipSuggestions([]);
      props.formikProps.setFieldValue(FIELDS.ZIPCODE, "");
      props.formikProps.resetForm();
    };

    window.addEventListener("pageshow", clearField);

    return () => window.removeEventListener("pageshow", clearField);
  }, []);

  const handleSelectChange =
    (formikProps: FormikProps<FormikValues>) =>
    (input: { name: string; value: string }) => {
      formikProps.setFieldValue(input.name, input.value);
      formikProps.submitForm();
      return null;
    };

  return (
    <Styled.ZipForm className={props.className} {...fallbackFormProps}>
      <Styled.FormContent isHorizontal={props.isHorizontal}>
        <Styled.SelectWrapper>
          {isBrowser ? (
            <FormikSelectExtended
              testId={HOMEPAGE.ZIP_INPUT}
              autoFocus={props.autoFocus}
              placeholder={props.translations.fieldPlaceholder}
              hideDropdownIndicator
              options={props.zipSuggestions}
              name={FIELDS.ZIPCODE}
              value={props.formikProps.values[FIELDS.ZIPCODE] || null}
              clearInput={
                !props.formikProps.values[FIELDS.ZIPCODE] &&
                !props.zipSuggestions
              }
              onInputChange={(value: string) => {
                // abort previous API call
                if (value !== "" && abortController) {
                  abortController.abort();
                }

                handleOnInputChange({
                  value,
                  setFieldValue: props.formikProps.setFieldValue,
                  serviceType: props.serviceType,
                  setZipSuggestions: props.setZipSuggestions,
                  setZipSuggestionsFetchingState:
                    props.setLoadingSuggestionsState,
                  setLoadingAbortController: setAbortController,
                });
              }}
              onChange={handleSelectChange(props.formikProps)}
            />
          ) : (
            /**
             * fallback input when JS haven't loaded yet on the client side
             */
            <Styled.InputWrapper>
              <FormikInputWithError
                isLabelFloating={false}
                placeholder={props.translations.fieldPlaceholder}
                name={FIELDS.ZIPCODE}
                value={props.formikProps.values[FIELDS.ZIPCODE]}
                handleChange={props.formikProps.handleChange}
              />
              <Styled.HiddenInput
                name="serviceType"
                value={props.serviceType}
                type={"hidden"}
              />
            </Styled.InputWrapper>
          )}
        </Styled.SelectWrapper>

        <TestWrapper testId={HOMEPAGE.LETS_GO_BTN}>
          <Styled.SubmitButton
            id={SMARTLOOK_IDS.START_BOOKING}
            type="submit"
            variant={props.buttonVariant}
            disabled={props.formikProps.isSubmitting}
            auto
            isHorizontal={props.isHorizontal}
            isLoading={props.loadingSuggestionsState === LOADING_STATE.LOADING}
            fitContent={props.buttonFitContent}
          >
            {props.translations.buttonLabel}
          </Styled.SubmitButton>
        </TestWrapper>
      </Styled.FormContent>
    </Styled.ZipForm>
  );
};

export { BasicProps, StartBookingFormView };
